// src/components/ClassSelect.js — Classes (events & programs) for tagging income and expenses
// [marker: books-pro v1]
// <ClassSelect value onChange placeholder? />   dropdown grouped by parent class (Events newest first)
// useClasses() → { classes, reload }            <Classes canEdit />   the Bookkeeping → Classes tab
import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

export const CATEGORY_LABELS = { program: 'Program services', management: 'Management & general', fundraising: 'Fundraising' };

let cache = { data: null, at: 0 };
const listeners = new Set();
export function invalidateClasses() { cache = { data: null, at: 0 }; listeners.forEach(fn => fn()); }

export function useClasses() {
  const [classes, setClasses] = useState(cache.data || []);
  const reload = useCallback(() => api.get('/classes').then(r => { cache = { data: r.data || [], at: Date.now() }; setClasses(cache.data); return cache.data; }).catch(() => []), []);
  useEffect(() => {
    if (cache.data && Date.now() - cache.at < 30000) setClasses(cache.data); else reload();
    listeners.add(reload);
    return () => listeners.delete(reload);
  }, [reload]);
  return { classes, reload };
}

export function ClassSelect({ value, onChange, placeholder = '— Class —', disabled, style, title }) {
  const { classes } = useClasses();
  const tops = classes.filter(c => !c.parent_id && (c.is_active || c.id === value));
  const kids = (id) => classes.filter(c => c.parent_id === id && (c.is_active || c.id === value));
  return (
    <select className="form-select" value={value || ''} disabled={disabled} style={style} title={title || 'Class — which event or program this is for'}
            onChange={e => onChange(e.target.value ? parseInt(e.target.value, 10) : null)}>
      <option value="">{placeholder}</option>
      {tops.filter(t => !kids(t.id).length).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      {tops.filter(t => kids(t.id).length).map(t => (
        <optgroup key={t.id} label={t.name}>
          <option value={t.id}>{t.name} (general)</option>
          {kids(t.id).map(c => <option key={c.id} value={c.id}>{'   '}{c.name}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

// ─── Bookkeeping → Classes ────────────────────────────────────────────────────
export default function Classes({ canEdit }) {
  const { classes, reload } = useClasses();
  const [editing, setEditing] = useState(null); // class | 'new'
  const [showInactive, setShowInactive] = useState(false);
  useEffect(() => { reload(); }, [reload]);
  const shown = classes.filter(c => showInactive || c.is_active);
  const tops = shown.filter(c => !c.parent_id);
  const kids = (id) => shown.filter(c => c.parent_id === id);
  const fmtDay = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

  const Row = ({ c, depth }) => (
    <tr style={{ cursor: canEdit ? 'pointer' : undefined, opacity: c.is_active ? 1 : 0.55 }} onClick={() => canEdit && setEditing(c)}>
      <td style={{ paddingLeft: 12 + depth * 22 }}>{depth ? <>↳ {c.name}</> : <strong>{c.name}</strong>}{!c.is_active && <span style={{ color: 'var(--gray-500)' }}> (inactive)</span>}</td>
      <td style={{ color: 'var(--gray-600)' }}>{c.event_id ? `Event · ${fmtDay(c.event_date)}` : depth ? '' : 'Group'}</td>
      <td>{c.category ? CATEGORY_LABELS[c.category] : c.effective_category ? <span style={{ color: 'var(--gray-500)' }}>{CATEGORY_LABELS[c.effective_category]} (from {c.parent_name})</span> : <span style={{ color: '#b45309' }}>Not set</span>}</td>
      <td style={{ textAlign: 'right' }}>{c.uses || ''}</td>
    </tr>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--gray-600)', maxWidth: 760 }}>
          Classes say <em>what</em> income and expenses were for — an event or a program — across every account. Every event gets its own class automatically,
          and ticket and sponsorship income is tagged by itself. Pick a class on bills, checks, card charges and invoice lines.
          The <strong>990 category</strong> splits expenses into program services, management &amp; general, and fundraising (Reports → Functional Expenses).
        </p>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: '0.82rem', display: 'flex', gap: 5, alignItems: 'center' }}><input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} /> Show inactive</label>
          {canEdit && <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>+ New Class</button>}
        </span>
      </div>
      <div className="card">
        <div className="table-wrap" style={{ border: 'none' }}>
          <table>
            <thead><tr><th>Class</th><th>Kind</th><th>990 category</th><th style={{ textAlign: 'right' }}>Times used</th></tr></thead>
            <tbody>
              {tops.map(t => (
                <React.Fragment key={t.id}>
                  <Row c={t} depth={0} />
                  {kids(t.id).map(k => <Row key={k.id} c={k} depth={1} />)}
                </React.Fragment>
              ))}
              {!tops.length && <tr><td colSpan={4} style={{ padding: 20, color: 'var(--gray-500)' }}>No classes yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {editing && <ClassModal cls={editing === 'new' ? null : editing} classes={classes} onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); invalidateClasses(); reload(); }} />}
    </div>
  );
}

function ClassModal({ cls, classes, onClose, onSaved }) {
  const [form, setForm] = useState({ name: cls?.name || '', parentId: cls?.parent_id || '', category: cls?.category || '', isActive: cls ? cls.is_active : true });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const parents = classes.filter(c => !c.parent_id && c.id !== cls?.id);
  const save = async () => {
    setBusy(true);
    try {
      const body = { name: form.name, parentId: form.parentId || null, category: form.category || null, isActive: form.isActive };
      if (cls) await api.patch(`/classes/${cls.id}`, body); else await api.post('/classes', body);
      toast.success('Class saved.'); onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Could not save.'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete the class “${cls.name}”?`)) return;
    try { await api.delete(`/classes/${cls.id}`); toast.success('Class deleted.'); onSaved(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not delete.'); }
  };
  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1500 }}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header"><span className="modal-title">{cls ? 'Edit Class' : 'New Class'}</span><button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          {cls?.event_id && <p className="form-hint" style={{ marginTop: 0 }}>This class belongs to the event “{cls.event_name}”.</p>}
          <div className="form-group"><label className="form-label">Name *</label>
            <input className="form-input" value={form.name} autoFocus onChange={e => set('name', e.target.value)} placeholder="e.g. Law Enforcement Briefing, Advocacy, Administration" /></div>
          <div className="form-group"><label className="form-label">Inside (optional)</label>
            <select className="form-select" value={form.parentId || ''} onChange={e => set('parentId', e.target.value ? parseInt(e.target.value, 10) : '')}>
              <option value="">— Top level —</option>
              {parents.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select></div>
          <div className="form-group"><label className="form-label">990 category</label>
            <select className="form-select" value={form.category} onChange={e => set('category', e.target.value)}>
              <option value="">{form.parentId ? '— Same as the group —' : '— Not set —'}</option>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <p className="form-hint">Your accountant can tell you which events count as program services and which as fundraising.</p></div>
          {cls && <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.875rem' }}>
            <input type="checkbox" checked={form.isActive} onChange={e => set('isActive', e.target.checked)} /> Active (inactive classes are hidden from the pick lists)</label>}
        </div>
        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <span>{cls && !cls.event_id && <button className="btn btn-ghost" style={{ color: 'var(--danger)' }} onClick={remove}>Delete</button>}</span>
          <span style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={busy}>Save</button>
          </span>
        </div>
      </div>
    </div>
  );
}
